import { queueDisplaySave } from 'helpers/display_saves';
import ApplicationController from './application_controller';

// The gear on a goal's "Progress this period" band on a progress report: what the goal's chart
// draws. Six switches, one per option in ProgressReport::GOAL_GRAPH_OPTIONS, each starting as
// DEFAULTS says (ProgressReport::GOAL_GRAPH_DEFAULTS: the chart and its lines on, the band, the
// summary and the list asked for):
//
//   graph        the chart and its key, or a one-line note in their place (the data points stay)
//   trend        the rolling-average halo
//   references   the baseline and target lines
//   period       the band shading the sessions inside the reporting period, over whatever is shown
//   summary      the lines under the chart saying where the sessions went, first to last
//   points       the list of every session under the chart; the twisty on the band is the same choice
//
// and beneath them the dates the chart covers (the range): the reporting period ('period', the
// default), every signed session ('all'), or dates of the goal's own ('custom'), picked with two
// buttons or by editing the two date fields.
//
// The element carries the goal's FULL chart config (everything the goal has, each session with
// its ISO day) beside the values; the chart element under it carries the config as drawn. A
// switch or a change of range rewrites the drawn config from the full one, exactly as
// ProgressReports::GoalChart.apply does on the server, and the chart controller redraws from its
// changed value. The key's entries (each carrying its mark in data-mark, see
// patients/goals_v3/_chart_legend) are hidden or shown to match what is drawn, so the key never
// names a line that is not on screen, and the band's words follow the chart: the `periodText`
// elements speak for the dates covered (their title, count and first-to-last line rewritten for
// the range in force), the `historyText` elements for every session, and one set shows at a
// time. Narrowing needs two sessions in the dates (a single session is a dot, not a chart); with
// fewer, every session shows and the note under the fields says so.
//
// Every change is saved to the report (PATCH, JSON) when the element carries a url (the signed
// page), and written into the `field` hidden inputs when the band has them (the fill page, where
// the choice travels with the report's own form and saves with it). Each change is announced as
// `goal-graph-options:changed` so the page-wide gear (goals-report-graphs) can keep its own
// controls in step; a save that does not go through (a session that ended, a change the report
// refused) is announced as `goal-graph-options:save-failed`, and the page-wide gear says so on
// the page, since the chart has already changed and the PDF would print the saved state. A change
// the page-wide gear makes arrives as the values changing, so it is applied (fields included) but
// never saved from here.
//
// Hiding the graph announces `goal-graph-toggle:toggled`, the event the Goals v3 Hide graph toggle
// sends, for anything on the page that listens (the data-point list under the chart does not: it
// opens when the therapist opens it, by the twisty or the switch). It fires on a change, never on
// connect.
//
// Targets:
//   chart        the goal-progress-chart element (its config value is rewritten)
//   graph        the chart wrapper and the key (hidden together when the graph is off)
//   note         the "Graph hidden" line shown in their place
//   periodText   the band's words for the dates covered
//   periodTitle  "Progress this period" / "Progress from 6/1/2026 to 8/31/2026"
//   periodCount  "3 data points"
//   periodLine   "41% on 6/10/2026 → 77% on 6/30/2026"
//   historyText  the band's words for every session
//   summary      the summary lines under the chart (shown only with the summary switch on)
//   pointsList   the data-point list (shown only with the points choice on)
//   pointsButton the twisty that opens and closes it (aria-expanded and the title follow)
//   pointsCaret  the caret inside the twisty (is-open while the list is open)
//   switch       a checkbox in the popover, carrying data-option (an OPTIONS key)
//   field        the hidden input of the report's form carrying the goal's whole choice, as JSON
//   pick         a range button, carrying data-range ('period' | 'all')
//   fromInput    the From date field
//   toInput      the To date field
//   rangeNote    whose dates are in force
// Values:
//   goal        the goal's id, the key the choice is saved under
//   url         where to save (absent when the viewer may not change the report)
//   config      the full chart config
//   range       'period' | 'all' | 'custom'
//   from, to    the goal's own dates, ISO, for the custom range
//   reportFrom, reportTo  the report's own dates, ISO
export const OPTIONS = ['graph', 'trend', 'references', 'period', 'summary', 'points'];
export const DEFAULTS = { graph: true, trend: true, references: true, period: false, summary: false, points: false };
export const RANGES = ['period', 'all', 'custom'];
export const RANGE = ['from', 'to'];

// The Stimulus value an option is kept in: period_only lives in periodOnly.
export const valueName = (option) => option.replace(/_(\w)/g, (match, letter) => letter.toUpperCase());

// "2026-06-01" as the band writes dates, "6/1/2026".
export const formatDay = (iso) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!match) return '';
  return `${Number(match[2])}/${Number(match[3])}/${match[1]}`;
};

// Sessions needed before the trend halo is drawn, and before a range can stand alone; the same
// numbers as ProgressReports::GoalChart.
const TREND_MIN_SESSIONS = 3;
const PERIOD_MIN_SESSIONS = 2;

export default class extends ApplicationController {
  static targets = [
    'chart',
    'graph',
    'note',
    'periodText',
    'periodTitle',
    'periodCount',
    'periodLine',
    'historyText',
    'summary',
    'pointsList',
    'pointsButton',
    'pointsCaret',
    'switch',
    'field',
    'pick',
    'fromInput',
    'toInput',
    'rangeNote',
  ];

  static values = {
    goal: String,
    url: String,
    config: Object,
    graph: { type: Boolean, default: DEFAULTS.graph },
    trend: { type: Boolean, default: DEFAULTS.trend },
    references: { type: Boolean, default: DEFAULTS.references },
    period: { type: Boolean, default: DEFAULTS.period },
    summary: { type: Boolean, default: DEFAULTS.summary },
    points: { type: Boolean, default: DEFAULTS.points },
    range: { type: String, default: 'period' },
    from: { type: String, default: '' },
    to: { type: String, default: '' },
    reportFrom: { type: String, default: '' },
    reportTo: { type: String, default: '' },
  };

  initialize() {
    this.lastHidden = !this.graphValue;
  }

  connect() {
    this.connected = true;
    this.apply();
  }

  disconnect() {
    this.connected = false;
  }

  // A switch in the popover.
  change(event) {
    const { option } = event.target.dataset;
    if (!OPTIONS.includes(option)) return;
    this[`${valueName(option)}Value`] = event.target.checked;
    this.announce();
  }

  // The twisty on the band: the same choice as the Data points switch.
  togglePoints() {
    this.pointsValue = !this.pointsValue;
    this.announce();
  }

  // This period or All.
  pickRange(event) {
    const { range } = event.currentTarget.dataset;
    if (!['period', 'all'].includes(range)) return;
    this.rangeValue = range;
    this.fromValue = '';
    this.toValue = '';
    this.announce();
  }

  // A date field in the popover: dates of the goal's own, both ends kept. A pair that ends before
  // it starts is put back the way it was, the same as the server would leave it.
  changeRange() {
    const from = this.hasFromInputTarget ? this.fromInputTarget.value : '';
    const to = this.hasToInputTarget ? this.toInputTarget.value : '';
    if (!from || !to || from > to) {
      this.apply();
      return;
    }
    // The report's own dates are the reporting period: nothing of the goal's to keep.
    if (from === this.reportFromValue && to === this.reportToValue) {
      this.rangeValue = 'period';
      this.fromValue = '';
      this.toValue = '';
    } else {
      this.rangeValue = 'custom';
      this.fromValue = from;
      this.toValue = to;
    }
    this.announce();
  }

  announce() {
    const options = this.options();
    this.save({ [this.goalValue]: options });
    this.dispatch('changed', { detail: { goal: this.goalValue, options } });
  }

  graphValueChanged() {
    this.apply();
  }

  periodValueChanged() {
    this.apply();
  }

  trendValueChanged() {
    this.apply();
  }

  referencesValueChanged() {
    this.apply();
  }

  summaryValueChanged() {
    this.apply();
  }

  pointsValueChanged() {
    this.apply();
  }

  rangeValueChanged() {
    this.apply();
  }

  fromValueChanged() {
    this.apply();
  }

  toValueChanged() {
    this.apply();
  }

  // The four switches, the range and its dates, as saved: a date the goal does not have of its
  // own is null.
  options() {
    const options = OPTIONS.reduce((all, option) => ({ ...all, [option]: this[`${valueName(option)}Value`] }), {});
    options.range = RANGES.includes(this.rangeValue) ? this.rangeValue : 'period';
    RANGE.forEach((end) => {
      options[end] = options.range === 'custom' ? this[`${end}Value`] || null : null;
    });
    return options;
  }

  // The dates the chart covers, by the range: the report's own, every session (the first to the
  // last), or the goal's own (either end falling back to the report's).
  dates() {
    const range = RANGES.includes(this.rangeValue) ? this.rangeValue : 'period';
    const points = (this.configValue || {}).points || [];
    if (range === 'all') {
      return {
        range,
        from: points.length ? points[0].day : '',
        to: points.length ? points[points.length - 1].day : '',
      };
    }
    if (range === 'custom') {
      return { range, from: this.fromValue || this.reportFromValue, to: this.toValue || this.reportToValue };
    }
    return { range, from: this.reportFromValue, to: this.reportToValue };
  }

  // The page as the options say. The value callbacks fire once before connect with the initial
  // values, which the server already rendered: nothing is rewritten or announced until connect.
  apply() {
    const on = this.options();
    const hidden = !on.graph;
    const full = this.configValue || {};
    const dates = this.dates();
    const range = this.rangeFor(full, dates);
    const drawn = this.drawnConfig(on, range);
    const narrowed = dates.range !== 'all' && this.narrowable(range);

    this.switchTargets.forEach((element) => {
      const { option } = element.dataset;
      if (!OPTIONS.includes(option)) return;
      element.checked = on[option];
    });
    // The report's form, kept in step: the fill page saves the choice with the report, the whole of
    // it in one field as the signed page's PATCH carries it.
    this.fieldTargets.forEach((field) => {
      field.value = JSON.stringify(on);
    });
    this.pickTargets.forEach((element) => {
      const active = element.dataset.range === dates.range;
      element.classList.toggle('active', active);
      element.setAttribute('aria-pressed', String(active));
    });
    if (this.hasFromInputTarget) {
      this.fromInputTarget.value = dates.from;
      this.fromInputTarget.max = dates.to;
    }
    if (this.hasToInputTarget) {
      this.toInputTarget.value = dates.to;
      this.toInputTarget.min = dates.from;
    }
    this.rangeNoteTargets.forEach((element) => {
      element.textContent = this.rangeNote(dates, narrowed);
    });
    if (!this.connected) return;

    this.graphTargets.forEach((element) => {
      element.hidden = hidden;
    });
    this.noteTargets.forEach((element) => {
      element.hidden = !hidden;
    });
    // With no chart to narrow (too few sessions in all) the band speaks for the period, as it did.
    const whole = this.hasChartTarget && !narrowed;
    this.periodTextTargets.forEach((element) => {
      element.hidden = whole;
    });
    this.historyTextTargets.forEach((element) => {
      element.hidden = !whole;
    });
    // The summary lines are among those, and stay out of sight until asked for.
    this.summaryTargets.forEach((element) => {
      if (!on.summary) element.hidden = true;
    });
    // The data-point list, by the twisty or the switch.
    this.pointsListTargets.forEach((element) => {
      element.hidden = !on.points;
    });
    this.pointsButtonTargets.forEach((button) => {
      button.setAttribute('aria-expanded', String(on.points));
      button.title = on.points ? 'Hide the individual sessions' : 'Show the individual sessions';
    });
    this.pointsCaretTargets.forEach((caret) => {
      caret.classList.toggle('is-open', on.points);
    });
    if (this.hasChartTarget) this.speak(full, range, dates);
    this.element.querySelectorAll('[data-mark]').forEach((element) => {
      element.hidden = !this.markDrawn(element.dataset.mark, drawn);
    });
    if (this.hasChartTarget) {
      const json = JSON.stringify(drawn);
      // Compared as parsed JSON: the server writes 40.0 where this writes 40, and rewriting an
      // unchanged config would only make the chart redraw itself.
      if (this.normalized(this.chartTarget.dataset.goalProgressChartConfigValue) !== json) {
        this.chartTarget.dataset.goalProgressChartConfigValue = json;
      }
    }
    if (hidden !== this.lastHidden) {
      this.lastHidden = hidden;
      this.dispatch('toggled', { prefix: 'goal-graph-toggle', detail: { hidden } });
    }
  }

  // Whose dates the chart covers, under the fields; the same words ProgressReportsHelper writes.
  rangeNote(dates, narrowed) {
    if (dates.range === 'all') return 'Every signed session.';
    if (dates.range === 'custom') {
      return narrowed ? 'Chosen dates.' : 'Fewer than two sessions in these dates, so every session shows.';
    }
    return narrowed ? "The report's dates." : 'Fewer than two sessions in the period, so every session shows.';
  }

  // The covered dates' words, the way the band renders them.
  speak(full, range, dates) {
    const points = range ? (full.points || []).slice(range.from, range.to + 1) : [];
    this.periodTitleTargets.forEach((element) => {
      element.textContent =
        dates.range === 'custom'
          ? `Progress from ${formatDay(dates.from)} to ${formatDay(dates.to)}`
          : 'Progress this period';
    });
    this.periodCountTargets.forEach((element) => {
      element.textContent = points.length
        ? `${points.length} data point${points.length === 1 ? '' : 's'}`
        : 'no signed data in the period';
    });
    this.periodLineTargets.forEach((element) => {
      element.textContent = this.line(points);
    });
  }

  line(points) {
    const say = (point) => `${point.tip} on ${formatDay(point.day)}`;
    if (points.length >= 2) return `${say(points[0])} → ${say(points[points.length - 1])}`;
    if (points.length === 1) return `${say(points[0])} (one data point)`;
    return 'no signed sessions';
  }

  // The sessions inside the dates covered, first to last, as indices into the full config's
  // points: the reporting period's own range for the period, none for every session.
  rangeFor(full, dates) {
    if (dates.range === 'all') return null;
    if (dates.range !== 'custom') return full.period || null;
    const points = full.points || [];
    const inside = [];
    points.forEach((point, index) => {
      if (point.day && point.day >= dates.from && point.day <= dates.to) inside.push(index);
    });
    if (!inside.length) return null;
    return { from: inside[0], to: inside[inside.length - 1], label: 'Chosen dates' };
  }

  // The full config with the switched-off parts taken out, the way GoalChart.apply leaves it:
  // the points narrowed to the range when it can stand alone, the reporting period's band
  // re-based onto them, a trend the config never had staying off whatever the switch says, and
  // so one with too few sessions left under it. The target each session was held to (refs, one
  // per point) and the marks where it changed (events, indices into the points) go with the
  // baseline and target lines: narrowed with the sessions, and off with them.
  drawnConfig(on, range) {
    const full = this.configValue || {};
    const narrow = on.range !== 'all' && this.narrowable(range);
    const points = narrow ? (full.points || []).slice(range.from, range.to + 1) : full.points || [];
    const refs = narrow ? (full.refs || []).slice(range.from, range.to + 1) : full.refs || [];
    const drawn = {
      ...full,
      points,
      trend: Boolean(full.trend) && on.trend && points.length >= TREND_MIN_SESSIONS,
      period: on.period ? this.rebased(full.period, narrow ? range : null) : null,
      baseline: on.references && full.baseline != null ? full.baseline : null,
      target: on.references && full.target != null ? full.target : null,
      refs: on.references ? refs : [],
      events: on.references ? this.rebasedEvents(full.events, narrow ? range : null) : [],
    };
    // A range's target start goes with the target line (only a range's config carries one).
    if ('target_low' in full) drawn.target_low = on.references && full.target_low != null ? full.target_low : null;
    return drawn;
  }

  // The changes of target as indices into the drawn sessions: every one when everything is drawn,
  // otherwise those falling on the slice, shifted to start where it starts.
  rebasedEvents(events, slice) {
    const all = events || [];
    if (slice == null) return all;
    return all
      .filter((event) => event.index >= slice.from && event.index <= slice.to)
      .map((event) => ({ ...event, index: event.index - slice.from }));
  }

  // The reporting period's band as indices into the drawn sessions: the full config's own when
  // everything is drawn, otherwise the part of it inside the slice, shifted to start where the
  // slice starts; null when none of it does.
  rebased(period, slice) {
    if (period == null) return null;
    if (slice == null) return period;
    const from = Math.max(period.from, slice.from);
    const to = Math.min(period.to, slice.to);
    if (from > to) return null;
    return { ...period, from: from - slice.from, to: to - slice.from };
  }

  // Whether the chart can narrow to a range alone: it holds enough sessions.
  narrowable(range) {
    return Boolean(range) && range.from != null && range.to != null && range.to - range.from + 1 >= PERIOD_MIN_SESSIONS;
  }

  // Whether a key entry names something the drawn config draws. The session data always does, and
  // the target does while either line draws it: the goal's own, or the one its sessions were held to.
  markDrawn(mark, drawn) {
    switch (mark) {
      case 'trend':
        return Boolean(drawn.trend);
      case 'period':
        return drawn.period != null;
      case 'baseline':
        return drawn.baseline != null;
      case 'target':
        return drawn.target != null || (drawn.refs || []).some((ref) => ref && ref.target != null);
      case 'revision':
        return (drawn.events || []).length > 0;
      default:
        return true;
    }
  }

  // A config attribute as JSON.stringify would write it, or null when it does not parse.
  normalized(json) {
    if (!json) return null;
    try {
      return JSON.stringify(JSON.parse(json));
    } catch {
      return null;
    }
  }

  // One at a time with the page's other display saves (helpers/display_saves).
  save(graphs) {
    if (!this.hasUrlValue || !this.urlValue) return;
    const url = this.urlValue;
    queueDisplaySave(() => {
      const token = document.querySelector('meta[name="csrf-token"]');
      return fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': token ? token.getAttribute('content') : '',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ graphs }),
      });
    })
      .then((response) => {
        // A redirect is the sign-in page: the session ended and nothing was saved.
        if (!response.ok || response.redirected) throw new Error(`Graph options not saved (${response.status})`);
      })
      .catch(() => this.dispatch('save-failed', { detail: { goal: this.goalValue } }));
  }
}
